import { useLocation, useNavigate } from "@tanstack/react-router"
import { useMemo } from "react"
import { useAuth } from "@/features/login/context/AuthContext"
import { Sidebar, SidebarContent, SidebarHeader, SidebarFooter, SidebarMenu } from "@/components/ui/sidebar"
import { SidebarHeaderComponent } from "./sidebar-header"
import { SidebarMenuItemComponent } from "./sidebar-menu-item"
import { SidebarRecentChatsComponent } from "./sidebar-recent-chats"
import { SidebarUserProfileComponent } from "./sidebar-user-profile"
import { getNavigationConfig, type SidebarMode } from "./navigation-config"
import { useNbiNavigationSafe } from "@/features/nbi/context/NbiNavigationContext"
import { useDocumentCountSafe } from "@/features/nbi/context/DocumentCountContext"
import { useNbiFormContextSafe } from "@/features/nbi/context/NbiFormContext"
import { getAllLoanAmountsFieldsErrors } from "@/features/nbi/utils/validators"
import { useChatHistoryContext } from "@/features/ask/context/ChatHistoryContext"
import { useSchemaEditSafe, toDisplayName } from "@/features/schema-management/context/SchemaEditContext"
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar"
import { Badge } from "@/components/ui/badge"
import { Check } from "lucide-react"
interface AppSidebarProps {
  isCollapsed: boolean
  isMobileOpen?: boolean
}

export default function AppSidebar({ isCollapsed, isMobileOpen = false }: AppSidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { email, logout, isAMLAnalyst, toggleUserRole } = useAuth()

  const handleLogout = () => {
    logout()
    navigate({ to: "/" })
  }

  const mode: SidebarMode = useMemo(() => {
    if (location.pathname.includes("/agent/application")) {
      return "banker-interface"
    }
    if (location.pathname.includes("/agent/document")) {
      return "schema-management"
    }
    return "policy-engine"
  }, [location.pathname])

  const isInsideApplicationFlow = useMemo(() => {
    return typeof location.search === 'object' &&
      location.search !== null &&
      'step' in location.search &&
      location.search.step !== undefined
  }, [location.search])

  const navConfig = getNavigationConfig(mode, { isInsideApplicationFlow })

  const nbiNavigation = useNbiNavigationSafe()
  const documentCount = useDocumentCountSafe()
  const formContext = useNbiFormContextSafe();

  const isApplicationSubmitted = formContext?.form.state.values.submitted === true

  const filteredNavItems = useMemo(() => {
    if (!isInsideApplicationFlow) return navConfig.items
    return navConfig.items.filter((item) => {
      if (item.title === "Outcome" && !isApplicationSubmitted) return false
      return true
    })
  }, [navConfig.items, isInsideApplicationFlow, isApplicationSubmitted])

  const user = useMemo(() => {
    const extractNameFromEmail = (emailAddress: string) => {
      if (!emailAddress) return "User"

      const localPart = emailAddress.split("@")[0]

      const nameParts = localPart
        .replace(/[._]/g, " ")
        .split(" ")
        .filter(Boolean)

      if (nameParts.length === 0) return "User"

      const capitalizedParts = nameParts.map(
        part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      )

      return capitalizedParts.join(" ")
    }

    return {
      name: extractNameFromEmail(email || ""),
      email: email || "",
      avatarUrl: undefined,
    }
  }, [email])

  const { chats: chatHistoryItems } = useChatHistoryContext()
  const schemaEdit = useSchemaEditSafe()
  const isSchemaEditRoute = location.pathname.includes("/agent/document/schema-edit")

  const activeChatId = useMemo(() => {
    if (typeof location.search === "object" && location.search !== null && "chat" in location.search) {
      return (location.search as Record<string, string>).chat
    }
    return undefined
  }, [location.search])

  const recentChats = useMemo(
    () => chatHistoryItems
      .filter((chat) => chat.title !== "New Chat")
      .map((chat) => ({
        id: chat.chatId,
        title: chat.title,
        url: `/agent/policy?chat=${chat.chatId}`,
      })),
    [chatHistoryItems],
  )

  return (
    <Sidebar
      collapsible="none"
      className={`h-screen bg-bg-subtle border-r border-semantic-border-default transition-all duration-200 overflow-hidden ${isMobileOpen ? "w-[56px]" : "w-0 border-r-0"
        } ${isCollapsed ? "md:w-[56px] md:border-r" : "md:w-[256px] md:border-r"}`}
    >
      <SidebarHeader className="p-4">
        <SidebarHeaderComponent
          mode={mode}
          onModeChange={mode !== "schema-management" ? (newMode) => {
            if (newMode === "banker-interface") {
              navigate({ to: "/agent/application" })
            } else {
              navigate({ to: "/agent/policy" })
            }
          } : undefined}
          isCollapsed={isCollapsed}
        />
      </SidebarHeader>
      <SidebarContent className={`flex flex-col gap-6 px-2 pt-6 items-center overflow-y-auto ${isCollapsed ? '' : 'md:items-start md:px-4'}`}>

        <div className="flex flex-col w-full">
          {navConfig.groupLabel && !isCollapsed && (
            <div className="hidden md:block px-3 py-[7px]">
              <span className="text-xs font-semibold leading-[18px] tracking-[0.18px] text-sidebar-muted">
                {navConfig.groupLabel}
              </span>
            </div>
          )}

          <SidebarMenu>
            {isSchemaEditRoute && schemaEdit ? (
              schemaEdit.documents.map((doc, index) => (
                <SidebarMenuItem key={doc.id}>
                  <SidebarMenuButton
                    isActive={index === schemaEdit.selectedDocIndex}
                    onClick={() => schemaEdit.setSelectedDocIndex(index)}
                    className="w-full h-8 rounded-md px-3 py-1 text-sm font-normal leading-[21px] tracking-[0.07px] text-text-subtle data-[active=true]:bg-bg-raised data-[active=true]:text-text-subtle cursor-pointer justify-start"
                  >
                    <span className={`flex-1 min-w-0 overflow-hidden whitespace-nowrap text-ellipsis ${isCollapsed ? "hidden" : ""}`}>
                      {toDisplayName(doc.documentType)}
                    </span>
                    {!isCollapsed && schemaEdit.completedDocIds.has(doc.id) && (
                      <Badge variant="success" className="shrink-0 min-h-5 px-2 py-0.5 flex items-center justify-center">
                        <Check className="w-3 h-3" />
                      </Badge>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))
            ) : (
              filteredNavItems.map((item) => {
                const normalizedPathname = location.pathname.replace(/\/$/, "")
                const normalizedItemUrl = item.url?.replace(/\/$/, "")

                return (
                  <SidebarMenuItemComponent
                    key={item.url || item.title}
                    item={item}
                    isActive={
                      mode === "banker-interface" && nbiNavigation && item.step !== undefined
                        ? item.step === nbiNavigation.currentStep
                        : !!normalizedItemUrl && normalizedPathname.endsWith(normalizedItemUrl) && (!item.action || !activeChatId)
                    }
                    isCollapsed={isCollapsed}
                    onStepClick={
                      mode === "banker-interface" && item.step !== undefined && nbiNavigation
                        ? () => nbiNavigation.setCurrentStep(item.step!)
                        : undefined
                    }
                    documentCount={
                      item.badge === "counter" && documentCount
                        ? { completed: documentCount.completedCount, total: documentCount.totalCount }
                        : undefined
                    }
                    validationBadge={{
                      amountsErrors: getAllLoanAmountsFieldsErrors(formContext?.form).length || 0,
                    }}
                  />
                )
              })
            )}
          </SidebarMenu>
        </div>

        {mode === "policy-engine" && !isCollapsed && recentChats.length > 0 && (
          <div className="hidden md:block w-full">
            <SidebarRecentChatsComponent
              chats={recentChats}
              activeChatId={activeChatId}
              onChatClick={(chatId) => navigate({ to: "/agent/policy", search: { chat: chatId } })}
            />
          </div>
        )}
      </SidebarContent>

      <SidebarFooter className={`p-4 justify-center ${isCollapsed ? '' : 'md:justify-start'}`}>
        <SidebarUserProfileComponent
          user={user}
          onLogout={handleLogout}
          isCollapsed={isCollapsed}
          isAMLAnalyst={isAMLAnalyst}
          onToggleUserRole={toggleUserRole}
        />
      </SidebarFooter>
    </Sidebar>
  )
}
